'use client';

import { createLogger } from '@/lib/debug';
import gtm from '@/lib/gtm';
import hubspot from '@/lib/hubspot';
import optimizely from '@/lib/optimizely';
import { OneTrustGeo } from '@/types/onetrust';
import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';

const logger = createLogger('onetrust');

export const OneTrustCategories = {
  Performance: 'C0002',
  Functional: 'C0003',
  TargetingAndSocialMedia: 'C0004',
  SocialMedia: 'C0005',
} as const;

export const ONETRUST_PERFORMANCE_CLASS = 'optanon-category-C0002';
interface OneTrustContextReturnType {
  geo?: OneTrustGeo;
  hasGroupConsent: (groupId: string) => boolean;
  isLoaded: boolean;
  showBanner: boolean;
  toggleInfoDisplay: () => void;
  isGDPR: boolean;
}

export const OneTrustContext = createContext<OneTrustContextReturnType>({
  geo: undefined,
  hasGroupConsent: (groupId) => false,
  isLoaded: false,
  showBanner: false,
  isGDPR: false,
  toggleInfoDisplay: () => {},
});

export const useOneTrustContext = (): OneTrustContextReturnType => {
  const activeGroups = useRef<string[]>([]);
  /**
   * Incremented whenever consent groups change. Active groups are stored in a ref
   * (activeGroups) so hasGroupConsent reads are synchronous — that way GTM and
   * other consent-gated scripts (e.g. behind PerfomanceConsentWrapper) see the
   * correct consent immediately on first load. Refs don’t trigger re-renders, so
   * we bump this state when groups change to force the provider and consumers to
   * re-render (e.g. after the user accepts cookies in the OneTrust preferences).
   */
  const [, setConsentVersion] = useState(0);
  const [geo, setGeo] = useState<OneTrustGeo | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isGDPR, setIsGDPR] = useState<boolean>(false);

  const onConsentChanged = useRef(() => {
    const sublogger = logger.withTag('onConsentChanged');

    if (!window.OnetrustActiveGroups) {
      sublogger.warn('Could not update consent in context, window.OnetrustActiveGroups was not set');

      return;
    }

    setIsGDPR(window.OneTrust.GetDomainData().ConsentModel.Name === 'opt-in');

    const isNoticeOnly = window.OneTrust.GetDomainData().ConsentModel.Name === 'notice only';
    const isBannerRequired =
      !isNoticeOnly && (window.OneTrust.GetDomainData().ShowAlertNotice || !window.OneTrust.GetDomainData().NoBanner);
    const isBannerDismissed = !isBannerRequired || window.OneTrust.IsAlertBoxClosedAndValid();

    setShowBanner(isBannerRequired && !isBannerDismissed);

    sublogger.log(`Consent updated: ${window.OnetrustActiveGroups}`);

    const newActiveGroups = window.OnetrustActiveGroups.split(',')
      .filter((str) => !!str)
      .map((str) => str.toUpperCase());

    const alwaysActiveGroups = window.OneTrust.GetDomainData()
      .Groups.filter((group) => group.Status === 'always active')
      .map((group) => group.OptanonGroupId);

    activeGroups.current = Array.from(new Set([...alwaysActiveGroups, ...newActiveGroups]));
    setConsentVersion((version) => version + 1);

    // Update consent for libs and third-party services

    // Set the consent as it stands right now _if_ they have consented
    if (isBannerDismissed) {
      sublogger.log(`Updating Google Consent`);

      const { GoogleConsent } = window.OneTrust.GetDomainData();

      // Update the users GTM consent based on their selections
      gtm.setConsent({
        ad_personalization: activeGroups.current.includes(GoogleConsent.GCAdPersonalization),
        // https://developer.onetrust.com/onetrust/docs/using-google-consent-mode#implementing-with-onetrust-cookie-compliance---no-gtm
        ad_storage: activeGroups.current.includes(OneTrustCategories.TargetingAndSocialMedia),
        ad_user_data: activeGroups.current.includes(GoogleConsent.GCAdUserData),
        // https://developer.onetrust.com/onetrust/docs/using-google-consent-mode#implementing-with-onetrust-cookie-compliance---no-gtm
        analytics_storage: activeGroups.current.includes(OneTrustCategories.Performance),
      });
    }

    optimizely.setConsent(activeGroups.current.includes(OneTrustCategories.Performance));

    hubspot.setConsent(activeGroups.current.includes(OneTrustCategories.Functional));
  });

  useEffect(() => {
    const onLoad = () => {
      logger.log('OneTrust onLoad triggered');

      const { GoogleConsent } = window.OneTrust.GetDomainData();

      setGeo(window.OneTrust.getGeolocationData());

      /*
       * Set the default consent in GTM based on the defaults set
       * in the geolocation rule that applies to the current user
       */
      gtm.setConsent(
        {
          ad_personalization: GoogleConsent.GCEnable ? getDefaultGroupStatus(GoogleConsent.GCAdPersonalization) : false,
          ad_storage: GoogleConsent.GCEnable ? getDefaultGroupStatus(GoogleConsent.GCAdStorage) : false,
          ad_user_data: GoogleConsent.GCEnable ? getDefaultGroupStatus(GoogleConsent.GCAdUserData) : false,
          analytics_storage: GoogleConsent.GCEnable ? getDefaultGroupStatus(GoogleConsent.GCAnalyticsStorage) : false,
        },
        true,
      );

      // Set the consent as it stands right now
      onConsentChanged.current();

      // Signal that everything is loaded and ready
      setIsLoaded(true);
    };

    try {
      if (window.OneTrust.GetDomainData().Domain) {
        // OneTrust is already loaded and ready, so run the callback right away
        onLoad();
      } else {
        throw new Error('OneTrust not ready');
      }
    } catch (e) {
      // OneTrust hasn't loaded yet, so set a callback to run once it's loaded and ready
      window.OptanonWrapper = onLoad;
    }

    const groupsUpdatedListener = () => {
      logger.log('Event triggered: OneTrustGroupsUpdated');
      onConsentChanged.current();
    };

    // https://my.onetrust.com/articles/en_US/Knowledge/UUID-bc6c634c-b234-acc7-21d3-fa2819a6e679
    window.addEventListener('OneTrustGroupsUpdated', groupsUpdatedListener);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', groupsUpdatedListener);
    };
  }, []);

  const hasGroupConsent = useRef((groupId: string) => {
    return activeGroups.current.includes(groupId.toUpperCase());
  }).current;

  const toggleInfoDisplay = useRef(() => window.OneTrust.ToggleInfoDisplay()).current;

  return {
    geo,
    hasGroupConsent,
    isLoaded,
    showBanner,
    toggleInfoDisplay,
    isGDPR,
  };
};

const getDefaultGroupStatus = (id: string) => {
  // DNAC stands for "Do not assign category" in OneTrust
  if (id === 'DNAC' || id === '') return false;

  const match = window.OneTrust.GetDomainData().Groups.find((group) => group.OptanonGroupId === id);

  if (!match) {
    throw new Error(`Could not find group with an ID of "${id}"`);
  }

  return match.Status === 'active' || match.Status === 'always active';
};

export const OneTrustConsentWrapper = ({ groupId, children }: PropsWithChildren<{ groupId?: string }>) => {
  const { hasGroupConsent, isLoaded } = useContext(OneTrustContext);

  if (!isLoaded) return null;

  return !groupId || hasGroupConsent(groupId) ? children : null;
};
